import React from 'react';
import { useParams } from 'react-router-dom';
import { useGetBadgeVerificationQuery } from '../../slices/badgeApi';
import { useGetBadgeMediaQuery } from '../../slices/credentialingApi';
import EmailVerification from './EmailVerification';
import './BadgeVerificationPage.css';

const BadgeVerificationPage = () => {
  const { credentialId } = useParams();

  const {
    data: verificationData,
    isLoading,
    error,
    refetch
  } = useGetBadgeVerificationQuery(credentialId, {
    skip: !credentialId
  });

  // Fetch badge media for the badge image
  const badgeId = verificationData?.badge?.id;
  console.log("Badge id for media fetch:", verificationData);
  const { data: badgeMedia } = useGetBadgeMediaQuery(badgeId, {
    skip: !badgeId
  });


  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="badge-verification">
        <div className="badge-verification__loading">
          <div className="badge-verification__spinner"></div>
          <p>Verifying badge...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="badge-verification">
        <div className="badge-verification__error">
          <div className="badge-verification__error-icon">❌</div>
          <h1>Badge Verification Failed</h1>
          <p>
            {error.status === 404 
              ? 'This badge could not be found or has been revoked.'
              : 'Unable to verify this badge. Please check the URL and try again.'
            }
          </p>
          <div className="badge-verification__error-details">
            <strong>Credential ID:</strong> {credentialId}
          </div>
          <button onClick={refetch} className="badge-verification__retry">
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!verificationData) {
    return (
      <div className="badge-verification">
        <div className="badge-verification__error">
          <h1>No Badge Data</h1>
          <p>No verification data available for this credential.</p>
        </div>
      </div>
    );
  }

  // Handle failed verification (API returns verified: false)
  if (verificationData.verified === false) {
    return (
      <div className="badge-verification">
        <div className="badge-verification__error">
          <div className="badge-verification__error-icon">❌</div>
          <h1>Badge Verification Failed</h1>
          <p>{verificationData.message || 'This badge could not be verified.'}</p>
          <div className="badge-verification__error-details">
            <strong>Credential ID:</strong> {credentialId}
            <br />
            <strong>Status:</strong> {verificationData.status}
          </div>
          <button onClick={refetch} className="badge-verification__retry">
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  // Handle the actual response structure from the backend
  const badge_data = verificationData.badge ? {
    badge_id: verificationData.badge?.id,
    name: verificationData.badge?.name,
    description: verificationData.badge?.description,
    badge_type: verificationData.badge?.badge_type,
    skills: verificationData.badge?.skills,
    competencies: verificationData.badge?.competencies,
    professional_relevance: verificationData.badge?.professional_relevance,
    recipient_name: verificationData.recipient?.name || "Educational Professional",
    recipient_email: verificationData.recipient?.email_hash, // Note: this is hashed
    credential_id: verificationData.credential_id,
    issued_date: verificationData.earned_at,
    expiry_date: verificationData.expires_at,
    evidence: verificationData.credential?.evidence // Get evidence from credential object
  } : null;

  const verification_status = {
    is_valid: verificationData.verified === true,
    signature_valid: verificationData.trust_indicators?.cryptographically_signed === true,
    is_revoked: verificationData.status === 'revoked',
    verified_at: verificationData.last_verified,
    status: verificationData.status,
    message: verificationData.message
  };

  const issuer_info = verificationData.issuer || {};

  // If we still don't have badge data, show error
  if (!badge_data) {
    return (
      <div className="badge-verification">
        <div className="badge-verification__error">
          <div className="badge-verification__error-icon">❌</div>
          <h1>Badge Data Not Available</h1>
          <p>The badge verification data could not be loaded. This might be because:</p>
          <ul>
            <li>The credential ID is invalid</li>
            <li>The badge has been revoked</li>
            <li>There's a temporary server issue</li>
          </ul>
          <div className="badge-verification__error-details">
            <strong>Credential ID:</strong> {credentialId}
          </div>
          <button onClick={refetch} className="badge-verification__retry">
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="badge-verification">
      <div className="badge-verification__header">
        <div className="badge-verification__status">
          {verification_status?.is_valid ? (
            <div className="badge-verification__status-valid">
              <span className="badge-verification__status-icon">✅</span>
              <h1>Verified Digital Badge</h1>
            </div>
          ) : (
            <div className="badge-verification__status-invalid">
              <span className="badge-verification__status-icon">❌</span>
              <h1>Invalid Badge</h1>
            </div>
          )}
        </div>
      </div>

      <div className="badge-verification__content">
        <div className="badge-verification__badge-info">
          <div className="badge-verification__badge-header">
            <div className="badge-verification__badge-icon">
              {(() => {
                const media = badgeMedia?.data || badgeMedia || {};
                const heroUrl = media.image_url || media.large_url || media.image || media.hero_url;
                const iconUrl = media.icon_url || media.iconUrl || media.icon;

                if (heroUrl) {
                  return (
                    <img
                      src={heroUrl}
                      alt={`${badge_data?.name} badge`}
                      className="badge-verification__badge-image"
                    />
                  );
                } else if (iconUrl) {
                  return (
                    <img
                      src={iconUrl}
                      alt={`${badge_data?.name} badge`}
                      className="badge-verification__badge-image"
                    />
                  );
                } else {
                  return (
                    <span className="badge-verification__badge-emoji" role="img" aria-label="Badge">
                      🏅
                    </span>
                  );
                }
              })()}
            </div>
            <div className="badge-verification__badge-details">
              <h2>{badge_data?.name}</h2>
              <p className="badge-verification__badge-description">
                {badge_data?.description}
              </p>
            </div>
          </div>

          {/* Currently no recipient for privacy reasons */}
          {/* <div className="badge-verification__recipient">
            <h3>Awarded To</h3>
            <div className="badge-verification__recipient-info">
              <strong>{badge_data?.recipient_name}</strong>
              {badge_data?.recipient_email && (
                <span className="badge-verification__recipient-email">
                  {badge_data?.recipient_email}
                </span>
              )}
            </div>
          </div> */}

          <div className="badge-verification__metadata">
            <div className="badge-verification__metadata-item">
              <strong>Issue Date:</strong>
              <span>{formatDate(badge_data?.issued_date)}</span>
            </div>
            {badge_data?.expiry_date && (
              <div className="badge-verification__metadata-item">
                <strong>Expires:</strong>
                <span>{formatDate(badge_data?.expiry_date)}</span>
              </div>
            )}
            <div className="badge-verification__metadata-item">
              <strong>Credential ID:</strong>
              <span className="badge-verification__credential-id">{badge_data?.credential_id}</span>
            </div>
          </div>

          {badge_data?.evidence && badge_data?.evidence?.length > 0 && (
            <div className="badge-verification__evidence">
              <h3>Evidence</h3>
              <ul>
                {badge_data?.evidence?.map((item, index) => (
                  <li key={index}>
                    <strong>{item?.name}:</strong> {item?.description}
                    {item?.url && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        View Evidence
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="badge-verification__issuer">
          <h3>Issued By</h3>
          <div className="badge-verification__issuer-info">
            <div className="badge-verification__issuer-details">
              <strong>{issuer_info?.name}</strong>
              {issuer_info?.url && (
                <a 
                  href={issuer_info.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="badge-verification__issuer-link"
                >
                  Visit Issuer Website
                </a>
              )}
              {issuer_info?.email && (
                <span className="badge-verification__issuer-email">
                  {issuer_info?.email}
                </span>
              )}
            </div>
            <img
              src={issuer_info?.logo || '/static/img/logo-issuer.png'}
              alt={`${issuer_info?.name} logo`}
              className="badge-verification__issuer-logo"
            />
          </div>
        </div>

        <div className="badge-verification__verification-details">
          <h3>Verification Details</h3>
          <div className="badge-verification__verification-grid">
            <div className="badge-verification__verification-item">
              <strong>Cryptographic Signature:</strong>
              <span className={verification_status?.signature_valid ? 'valid' : 'invalid'}>
                {verification_status?.signature_valid ? '✅ Valid' : '❌ Invalid'}
              </span>
            </div>
            <div className="badge-verification__verification-item">
              <strong>Badge Status:</strong>
              <span className={verification_status?.is_revoked ? 'revoked' : 'active'}>
                {verification_status?.is_revoked ? '❌ Revoked' : '✅ Active'}
              </span>
            </div>
            <div className="badge-verification__verification-item">
              <strong>Verification Time:</strong>
              <span>{formatDate(verification_status?.verified_at)}</span>
            </div>
          </div>
        </div>

        <EmailVerification credentialId={credentialId} />

      </div>

      <div className="badge-verification__footer">
        <p>
          This is an official digital badge issued in compliance with the Open Badges 3.0 standard.
          The cryptographic signature ensures the authenticity and integrity of this credential.
        </p>
        <p>
          <strong>Verification URL:</strong>{' '}
          <code>{window.location.href}</code>
        </p>
      </div>
    </div>
  );
};

export default BadgeVerificationPage;